'use client';

import { ScrollArea, Tooltip } from '@mantine/core';
import { useMemo } from 'react';

import type { CardLayoutKind, ImageField } from '../../_lib/types';

type SharedImages = {
  profilePicture: ImageField | null;
  coverPhoto: ImageField | null;
  companyLogo: ImageField | null;
};

function getAvailableLayouts(images: SharedImages): Set<CardLayoutKind> {
  const hasProfile = images.profilePicture != null;
  const hasLogo = images.companyLogo != null;
  const hasCover = images.coverPhoto != null;

  const available = new Set<CardLayoutKind>();

  if (hasProfile) {
    available.add('1A');
  }
  if (hasLogo) {
    available.add('1B');
  }
  if (hasCover) {
    available.add('1C');
  }
  if (hasProfile && hasLogo) {
    available.add('2A');
    available.add('2B');
  }
  if (hasProfile && hasCover) {
    available.add('2C');
  }
  if (hasCover && hasLogo) {
    available.add('2D');
  }
  if (hasProfile && hasLogo && hasCover) {
    available.add('3A');
  }

  return available;
}

type ImageRequirement = 'profile photo' | 'logo' | 'cover photo';

const LAYOUT_IMAGE_REQUIREMENTS: Record<CardLayoutKind, ImageRequirement[]> = {
  'blinq-logo': [],
  '1A': ['profile photo'],
  '1B': ['logo'],
  '1C': ['cover photo'],
  '2A': ['profile photo', 'logo'],
  '2B': ['profile photo', 'logo'],
  '2C': ['profile photo', 'cover photo'],
  '2D': ['cover photo', 'logo'],
  '3A': ['profile photo', 'logo', 'cover photo'],
};

function getMissingImagesLabel(layout: CardLayoutKind, images: SharedImages): string | null {
  const hasProfile = images.profilePicture != null;
  const hasLogo = images.companyLogo != null;
  const hasCover = images.coverPhoto != null;

  const has: Record<ImageRequirement, boolean> = {
    'profile photo': hasProfile,
    logo: hasLogo,
    'cover photo': hasCover,
  };

  const missing = LAYOUT_IMAGE_REQUIREMENTS[layout].filter((req) => !has[req]);

  if (missing.length === 0) {
    return null;
  }

  const formatted = missing.map((m) => `a ${m}`);

  if (formatted.length === 1) {
    return `Add ${formatted[0]} to use this layout`;
  }

  return `Add ${formatted.slice(0, -1).join(', ')} and ${formatted[formatted.length - 1]} to use this layout`;
}

import { carouselRoot, container, slide } from './LayoutSwitcherCarousel.css';
import { LayoutSwitcherSlide } from './LayoutSwitcherSlide';

// 'blinq-logo' (the no-image state) is intentionally omitted — it isn't a real
// selectable layout, only the derived display when a card has no images.
const ALL_LAYOUTS: CardLayoutKind[] = ['1A', '1B', '1C', '2A', '2B', '2C', '2D', '3A'];

type LayoutSwitcherCarouselProps = {
  cardColor: string;
  currentLayout: CardLayoutKind;
  disabled?: boolean;
  images: SharedImages;
  onSelect: (layout: CardLayoutKind) => void;
  /** Exposes the scrolling viewport so a host can add its own paging controls. */
  viewportRef?: React.Ref<HTMLDivElement>;
  onScrollPositionChange?: (position: { x: number; y: number }) => void;
};

export function LayoutSwitcherCarousel({
  cardColor,
  currentLayout,
  disabled = false,
  images,
  onSelect,
  viewportRef,
  onScrollPositionChange,
}: LayoutSwitcherCarouselProps) {
  const availableLayouts = useMemo(() => getAvailableLayouts(images), [images]);

  return (
    <ScrollArea
      className={carouselRoot}
      scrollbarSize={8}
      type="hover"
      viewportRef={viewportRef}
      onScrollPositionChange={onScrollPositionChange}
    >
      <div className={container}>
        {ALL_LAYOUTS.map((layout) => {
          const isDisabled = disabled || !availableLayouts.has(layout);
          const isSelected = currentLayout === layout;
          // The locked selected tile explains its lock badge; other disabled
          // tiles explain which images they're missing.
          const tooltipLabel =
            disabled && isSelected
              ? 'Provided by the template'
              : isDisabled
                ? getMissingImagesLabel(layout, images)
                : null;

          return (
            <div className={slide} key={layout}>
              <Tooltip
                disabled={!tooltipLabel}
                label={tooltipLabel}
                multiline
                position="top"
                w={200}
                withArrow
              >
                <div>
                  <LayoutSwitcherSlide
                    cardColor={cardColor}
                    disabled={isDisabled}
                    disabledReason={tooltipLabel}
                    images={images}
                    isSelected={isSelected}
                    layout={layout}
                    onSelect={onSelect}
                  />
                </div>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
